import { newrelic } from '@hen-site/lib/newrelic/client';
import {
  buildGoogleAdsGtagScriptUrl,
  GOOGLE_ADS_GTAG_SCRIPT_ID,
  GROAS_CONTENT_PARAM,
  GROAS_CONTENT_SCRIPT_ID,
  GROAS_CONTENT_SCRIPT_URL,
  LINKEDIN_INSIGHT_SCRIPT_ID,
  LINKEDIN_INSIGHT_SCRIPT_URL,
  META_PIXEL_SCRIPT_ID,
  META_PIXEL_SCRIPT_URL,
} from './constants';
import { installFbqStub } from './fbqStub';

const VENDOR_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * Google Ads conversion IDs only. `gtag.js` also loads a tag-manager container
 * when handed a container id, so the generic vendor charset is too wide here: a
 * tenant-config value could otherwise open the rail peel-safe forbids.
 */
const GOOGLE_ADS_ID_PATTERN = /^AW-[0-9]{1,32}$/;

/**
 * Returns whether a vendor id is a plausible Pixel / partner identifier.
 *
 * @param {string} id - Candidate identifier from tenant-config.
 * @returns {boolean} True when the id matches the conservative charset and length.
 */
const isPlausibleVendorId = (id: string): boolean => {
  return VENDOR_ID_PATTERN.test(id);
};

/**
 * Reports a rejected vendor id to New Relic without including the raw value.
 *
 * @param {'pixelId' | 'partnerId' | 'conversionId'} kind - Which identifier failed validation.
 * @returns {void}
 */
const reportInvalidVendorId = (
  kind: 'pixelId' | 'partnerId' | 'conversionId',
): void => {
  newrelic.noticeError?.(new Error('PaidSocialVendorIdInvalid'), { kind });
};

/**
 * Appends an async script once, keyed by element id. Any identifier reaching
 * `src` must already have passed `isPlausibleVendorId`.
 *
 * @param {string} id - Script element id used as the idempotency guard.
 * @param {string} src - Absolute script URL.
 * @returns {void}
 */
const injectScript = (id: string, src: string): void => {
  if (typeof document === 'undefined') {
    return;
  }

  if (document.getElementById(id)) {
    return;
  }

  const script = document.createElement('script');
  script.id = id;
  script.src = src;
  script.async = true;
  document.head.appendChild(script);
};

/**
 * Queues `fbq('init', pixelId)` and injects `fbevents.js`. Idempotent.
 * Does not emit `track` / `trackCustom` events.
 *
 * @param {string} pixelId - Meta Pixel identifier from tenant-config.
 * @returns {void}
 */
export const loadMetaPixel = (pixelId: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!isPlausibleVendorId(pixelId)) {
    reportInvalidVendorId('pixelId');
    return;
  }

  if (document.getElementById(META_PIXEL_SCRIPT_ID)) {
    return;
  }

  installFbqStub();
  window.fbq?.('init', pixelId);
  injectScript(META_PIXEL_SCRIPT_ID, META_PIXEL_SCRIPT_URL);
};

/**
 * Sets LinkedIn partner globals and injects Insight. Idempotent.
 * Does not call `lintrk`.
 *
 * @param {string} partnerId - LinkedIn Insight partner identifier from tenant-config.
 * @returns {void}
 */
export const loadLinkedInInsight = (partnerId: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!isPlausibleVendorId(partnerId)) {
    reportInvalidVendorId('partnerId');
    return;
  }

  if (document.getElementById(LINKEDIN_INSIGHT_SCRIPT_ID)) {
    return;
  }

  window._linkedin_partner_id = partnerId;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids ?? [];
  if (!window._linkedin_data_partner_ids.includes(partnerId)) {
    window._linkedin_data_partner_ids.push(partnerId);
  }

  injectScript(LINKEDIN_INSIGHT_SCRIPT_ID, LINKEDIN_INSIGHT_SCRIPT_URL);
};

/**
 * Installs Google's `gtag` on `window.dataLayer` and injects `gtag.js`.
 * Idempotent. Overwrites OneTrust's `window.gtag` shim so calls reach Google.
 * Does not emit conversion events.
 *
 * `window.gtag` is reassigned before the injected-script guard, so a repeat
 * call reclaims ownership from a late OneTrust shim without loading `gtag.js`
 * twice or queueing a second `config`. Callers rely on that ordering.
 *
 * @param {string} conversionId - Google Ads conversion ID from tenant-config.
 * @returns {void}
 */
export const loadGoogleAdsTag = (conversionId: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!GOOGLE_ADS_ID_PATTERN.test(conversionId)) {
    reportInvalidVendorId('conversionId');
    return;
  }

  window.dataLayer = window.dataLayer ?? [];
  const dataLayer = window.dataLayer;
  window.gtag = function gtag() {
    dataLayer.push(arguments);
  };

  if (document.getElementById(GOOGLE_ADS_GTAG_SCRIPT_ID)) {
    return;
  }

  window.gtag('js', new Date());
  window.gtag('config', conversionId);
  injectScript(
    GOOGLE_ADS_GTAG_SCRIPT_ID,
    buildGoogleAdsGtagScriptUrl(conversionId),
  );
};

/**
 * Injects the GROAS content script once. The shared helper only writes id,
 * src, and async, so this loader sets `data-groas-param` itself before the
 * element is appended. Does not call `fbq`, `lintrk`, or `gtag`.
 *
 * @returns {void}
 */
export const loadGroasContent = (): void => {
  if (typeof document === 'undefined') {
    return;
  }

  if (document.getElementById(GROAS_CONTENT_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement('script');
  script.id = GROAS_CONTENT_SCRIPT_ID;
  script.src = GROAS_CONTENT_SCRIPT_URL;
  script.async = true;
  script.type = 'text/javascript';
  script.setAttribute('data-groas-param', GROAS_CONTENT_PARAM);
  document.head.appendChild(script);
};
